import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, RichTextView } from '@ibe/ui';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { AuditTrailPanel } from '../../../components/resource-detail/audit-trail-panel.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { useSoftDeleteNotificationTemplate } from '../api/mutations.js';
import { useNotificationTemplateDetail } from '../api/queries.js';
import { CHANNEL, type NotificationTemplateDetail } from '../api/schemas.js';

/**
 * One notification template.
 *
 * The body is the bulk of the screen. For email it is sanitised HTML; for SMS
 * it is plain text with whitespace preserved so an operator can see the exact
 * segment boundaries.
 */

export function NotificationTemplateDetailPage(): React.JSX.Element {
  const { templateId = '' } = useParams();
  const query = useNotificationTemplateDetail(templateId);
  const softDelete = useSoftDeleteNotificationTemplate();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  return (
    <ResourceDetailPage<NotificationTemplateDetail>
      query={query}
      permission={PERMISSIONS.NOTIFICATION_READ}
      parent={{ to: '/notification-templates', label: 'Notification templates' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.NOTIFICATION_CONFIGURE}>
            <Button asChild variant="secondary">
              <Link to={`/notification-templates/${templateId}/edit`}>
                <Pencil className="h-4 w-4" aria-hidden="true" />
                Edit
              </Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.NOTIFICATION_CONFIGURE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirming(true);
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data) => (
        <>
          <DetailGrid>
            <DetailField label="Code">{data.code}</DetailField>
            <DetailField label="Channel">{data.channel}</DetailField>
            <DetailField label="Type">{data.type}</DetailField>
            <DetailField label="Default">{data.isDefault ? 'Yes' : 'No'}</DetailField>
            <DetailField label="Active">{data.isActive ? 'Yes' : 'No'}</DetailField>
            {data.propertyId !== null ? (
              <DetailField label="Property">
                <Link className="underline" to={`/properties/${data.propertyId}`}>
                  View property
                </Link>
              </DetailField>
            ) : null}
            {data.channel === CHANNEL.EMAIL ? (
              <DetailField label="Subject">{data.subject}</DetailField>
            ) : null}
            {data.channel === CHANNEL.EMAIL && data.fromAddress !== null ? (
              <DetailField label="From address">{data.fromAddress}</DetailField>
            ) : null}
            {data.channel === CHANNEL.EMAIL && data.fromName !== null ? (
              <DetailField label="From name">{data.fromName}</DetailField>
            ) : null}
          </DetailGrid>

          <section aria-labelledby="template-body" className="space-y-2">
            <h2 id="template-body" className="text-body font-semibold">
              Body
            </h2>
            {data.channel === CHANNEL.EMAIL ? (
              <div className="rounded-md border border-border p-4">
                <RichTextView html={data.body} emptyMessage="No body has been written yet." />
              </div>
            ) : (
              <div className="rounded-md border border-border bg-muted p-4">
                <p className="whitespace-pre-wrap font-mono text-sm">
                  {data.body === '' ? 'No body has been written yet.' : data.body}
                </p>
              </div>
            )}
          </section>

          {data.variables.length > 0 ? (
            <section aria-labelledby="template-variables" className="space-y-2">
              <h2 id="template-variables" className="text-body font-semibold">
                Variables
              </h2>
              <div className="rounded-md border border-border bg-muted p-4">
                <code className="text-sm text-muted-foreground">{data.variables.join(', ')}</code>
              </div>
            </section>
          ) : null}

          <AuditTrailPanel entityType="notification_template" entityId={data.id} />

          <ConfirmDialog
            open={confirming}
            onOpenChange={setConfirming}
            title="Delete notification template"
            description={`${data.name} will be removed. Its audit history and any dispatches that used it are retained.`}
            confirmLabel="Delete"
            destructive
            loading={softDelete.isPending}
            onConfirm={() => {
              softDelete.mutate(data.id, {
                onSuccess: () => {
                  setConfirming(false);
                  void navigate('/notification-templates');
                },
              });
            }}
          />
        </>
      )}
    </ResourceDetailPage>
  );
}
